import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { api } from '../api/endpoints'
import { Card, Empty, ErrorState, Loading } from '../components/StateViews'
import {
  DETECTED_LABEL,
  eventBadgeClass,
  eventLabel,
  formatDate,
  formatNumber,
  intermediaryLabel,
} from '../format'

export function Intermediary() {
  const { number = '' } = useParams()

  const intermediary = useQuery({
    queryKey: ['intermediary', number],
    queryFn: ({ signal }) => api.intermediary(number, signal),
    enabled: number !== '',
  })
  const history = useInfiniteQuery({
    queryKey: ['intermediary', number, 'history'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => api.intermediaryHistory(number, pageParam, signal),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: number !== '',
  })

  if (intermediary.isPending) return <Loading rows={8} />
  if (intermediary.isError) {
    return <ErrorState error={intermediary.error} onRetry={() => void intermediary.refetch()} />
  }

  const data = intermediary.data
  const events = history.data?.pages.flatMap((page) => page.data) ?? []

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500">{data.registration_number}</p>
        <h1 className="text-lg font-semibold">{intermediaryLabel(data)}</h1>
        <p className="text-sm text-slate-500">
          Sezione {data.section} ·{' '}
          {data.legal_form === 'PG' ? 'persona giuridica' : 'persona fisica'}
          {data.municipality ? ` · ${data.municipality}` : ''}{' '}
          {data.province ? `(${data.province})` : ''}
        </p>
      </div>

      {data.inactive && (
        <div className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
          Inoperativo dal {formatDate(data.inactive_since)}. La data è quella riportata dal registro
          e può essere retroattiva di mesi rispetto alla rilevazione.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Anagrafica" className="lg:col-span-1">
          <dl className="space-y-3 p-4 text-sm">
            <Entry label="Iscritto dal" value={formatDate(data.registered_on)} />
            <Entry label="Titolo sez. A" value={data.section_a_title ?? '—'} />
            <Entry label="Attività sez. A" value={data.section_a_activity ?? '—'} />
            <Entry label="Titolo sez. B" value={data.section_b_title ?? '—'} />
            <Entry label="Cariche registrate" value={formatNumber(data.positions)} />
            <Entry label="Visto nello snapshot del" value={formatDate(data.seen_on)} />
          </dl>
        </Card>

        <Card title={`Mandati attivi (${formatNumber(data.mandates.length)})`} className="lg:col-span-2">
          {data.mandates.length === 0 ? (
            <Empty title="Nessun mandato attivo." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Compagnia
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Codice
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      {DETECTED_LABEL}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.mandates.map((mandate) => (
                    <tr key={mandate.company_code}>
                      <td className="px-4 py-2">
                        <Link
                          to={`/companies/${mandate.company_code}`}
                          className="underline decoration-slate-300"
                        >
                          {mandate.company_name ?? mandate.company_code}
                        </Link>
                      </td>
                      <td className="px-4 py-2 tabular-nums">{mandate.company_code}</td>
                      <td className="px-4 py-2 tabular-nums">{formatDate(mandate.appeared_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={`Sedi (${formatNumber(data.offices.length)})`}>
          {data.offices.length === 0 ? (
            <Empty title="Nessuna sede registrata." />
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.offices.map((office, index) => (
                <li
                  key={`${office.address ?? ''}|${office.postal_code ?? ''}|${office.municipality ?? ''}|${index}`}
                  className="px-4 py-2"
                >
                  <span className="font-medium">{office.address ?? '—'}</span>
                  <span className="text-slate-500">
                    {' '}
                    · {office.postal_code ?? ''} {office.municipality ?? ''}{' '}
                    {office.province ? `(${office.province})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Siti internet (${formatNumber(data.websites.length)})`}>
          {data.websites.length === 0 ? (
            <Empty title="Nessun sito registrato." />
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.websites.map((website) => (
                <li key={website} className="break-all px-4 py-2">
                  {website}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Cronologia">
        <p className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
          Ogni riga è il giorno in cui il sistema ha visto il cambiamento nel registro, non il giorno
          in cui è avvenuto: IVASS lavora le pratiche con i suoi tempi.
        </p>
        {history.isPending ? (
          <Loading />
        ) : history.isError ? (
          <ErrorState error={history.error} onRetry={() => void history.refetch()} />
        ) : events.length === 0 ? (
          <Empty
            title="Nessun evento registrato."
            hint="La storia comincia dal primo snapshot applicato."
          />
        ) : (
          <>
            <ol className="divide-y divide-slate-100">
              {events.map((event) => (
                <li key={event.id} className="flex items-start gap-3 px-4 py-3 text-sm">
                  <span className="w-24 shrink-0 tabular-nums text-slate-500">
                    {formatDate(event.detected_on)}
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${eventBadgeClass(event.type)}`}
                  >
                    {eventLabel(event.type)}
                  </span>
                  <span className="text-slate-700">
                    {event.company?.name ?? ''}
                    {event.before && event.after && (
                      <span className="text-slate-500">
                        {' '}
                        {JSON.stringify(event.before)} → {JSON.stringify(event.after)}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
            {history.hasNextPage && (
              <div className="border-t border-slate-100 px-4 py-3 text-center">
                <button
                  type="button"
                  onClick={() => void history.fetchNextPage()}
                  disabled={history.isFetchingNextPage}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {history.isFetchingNextPage ? 'carico…' : 'carica altri'}
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}

function Entry({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}
